import { useEffect, useRef, useState } from 'react'
import styled, { css, FlattenSimpleInterpolation } from 'styled-components'

import { CssUnit } from '@/types'
import { breakpoints, colors } from '@/styles'
import { toUnit } from '@/utils/styles'

const Wrapper = styled.span<{
  top?: CssUnit
  right?: CssUnit
  bottom?: CssUnit
  left?: CssUnit
  lift?: boolean
  backgroundColor?: CssUnit
  password?: boolean
  customStyle?: FlattenSimpleInterpolation | undefined
}>`
  ${props =>
    !props.lift &&
    `
    position: relative;
  `}
  z-index: 10;
  display: inline-flex;
  margin-top: ${({ top }) => toUnit(top || 0)};
  margin-right: ${({ right }) => toUnit(right || 0)};
  margin-bottom: ${({ bottom }) => toUnit(bottom || 0)};
  margin-left: ${({ left }) => toUnit(left || 0)};

  ${({ customStyle }) => customStyle};
`

const passwordStyle = css`
  padding: 12px;
  background-color: ${colors.grey16};
  font-weight: 400;
  border-radius: 8px;
`

const topStyle = css`
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
`

const topStylePassword = css`
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-56%);
`

const topArrowStylePassword = css`
  position: absolute;
  width: 24px;
  height: 24px;
  left: calc(50% - 30px / 2);
  top: -30px;
  transform: rotate(45deg);
  background-color: ${colors.grey16};
  border-radius: 6px;
`

const topArrowStyle = css`
  top: -11px;
  left: calc(50% - 5px);
  border-right: 5px solid transparent;
  border-top: 8px solid ${colors.blue32};
  border-left: 5px solid transparent;
`

const bottomStyle = css`
  top: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
`
const bottomArrowStyle = css`
  bottom: -11px;
  left: calc(50% - 10px);
  border-right: 5px solid transparent;
  border-bottom: 8px solid ${colors.blue32};
  border-left: 5px solid transparent;
`

const rightStyle = css`
  top: 50%;
  left: calc(100% + 10px);
  transform: translateY(-50%);
`
const rightArrowStyle = css`
  top: calc(50% - 5px);
  right: -11px;
  border-right: 9px solid ${colors.blue32};
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
`

const leftStyle = css`
  top: 50%;
  right: calc(100% + 10px);
  transform: translateY(-50%);
`
const leftArrowStyle = css`
  top: calc(50% - 5px);
  left: -11px;
  border-left: 9px solid ${colors.blue32};
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
`

const topLeftStyle = css`
  bottom: calc(100% + 10px);
  right: calc(-50% + 15px);
`

const topRightStyle = css`
  bottom: calc(100% + 10px);
  left: calc(50% + 15px);
`

const bottomLeftStyle = css`
  top: calc(100% + 10px);
  right: calc(-50% + 15px);
`

const bottomRightStyle = css`
  top: calc(100% + 10px);
  left: calc(50% + 15px);
`

const Content = styled.span<{
  position?: string
  offsetLeft?: number
  offsetRight?: number
  backgroundColor?: CssUnit
  password?: boolean
}>`
  color: ${colors.white};
  position: absolute;
  z-index: 21;
  padding: 12px;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  text-transform: none;
  border-radius: 8px;
  white-space: nowrap;
  background-color: ${colors.blue32};
  animation: fadeIn ease-in-out 0.2s;
  ${props => props.password && passwordStyle};

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    font-size: 13px;
  }

  ${props => props.position === 'top' && topStyle};
  ${props => props.position === 'topLeft' && topLeftStyle};
  ${props => props.position === 'topRight' && topRightStyle};
  ${props => props.position === 'bottom' && bottomStyle};
  ${props => props.position === 'bottomLeft' && bottomLeftStyle};
  ${props => props.position === 'bottomRight' && bottomRightStyle};
  ${props => props.position === 'right' && rightStyle};
  ${props => props.position === 'left' && leftStyle};
  ${props => props.position === 'topPassword' && topStylePassword};

  ${props =>
    props.offsetLeft &&
    `
    left: ${props.offsetLeft}%;
  `};

  ${props =>
    props.offsetRight &&
    `
    right: ${props.offsetRight}%;
  `};
`

const Arrow = styled.span<{
  position: string
  lift: boolean
  password: boolean
}>`
  position: absolute;
  width: 0;
  height: 0;
  animation: fadeIn ease-in-out 0.2s;

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  ${props => props.position === 'top' && topArrowStyle};
  ${props => props.position === 'topLeft' && topArrowStyle};
  ${props => props.position === 'topRight' && topArrowStyle};
  ${props => props.position === 'bottom' && bottomArrowStyle};
  ${props => props.position === 'bottomLeft' && bottomArrowStyle};
  ${props => props.position === 'bottomRight' && bottomArrowStyle};
  ${props => props.position === 'right' && rightArrowStyle};
  ${props => props.position === 'left' && leftArrowStyle};
  ${props => props.password && topArrowStylePassword};
  ${props =>
    props.lift &&
    `
    left: auto;
    transform: translateX(7.5px);
  `}
`

type AnyEvent = MouseEvent | TouchEvent

interface Props {
  text: React.ReactNode
  children: React.ReactNode
  position: string
  lift?: boolean
  offsetLeft?: number
  offsetRight?: number
  top?: CssUnit
  right?: CssUnit
  bottom?: CssUnit
  left?: CssUnit
  backgroundColor?: CssUnit
  password?: boolean
  disabled?: boolean
  customStyle?: FlattenSimpleInterpolation | undefined
}

const Tooltip: React.FC<Props> = ({
  text,
  children,
  position = 'top',
  lift = false,
  password = false,
  disabled,
  ...rest
}) => {
  const node = useRef<HTMLSpanElement>(null)
  const [visible, setVisible] = useState(false)

  const handleClick = (event: AnyEvent) => {
    if (node?.current?.contains(event.target as Node)) {
      return
    }
    setVisible(false)
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClick)
    return () => {
      document.removeEventListener('mousedown', handleClick)
    }
  }, [])

  return (
    <Wrapper
      ref={node}
      lift={lift}
      onClick={() => setVisible(true)}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      {...rest}
    >
      {children}
      {visible && !disabled && (
        <>
          <Content position={position} password={password} {...rest}>
            {text}
          </Content>
          <Arrow lift={lift} position={position} password={password} />
        </>
      )}
    </Wrapper>
  )
}

export default Tooltip
